import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { 
  Plus, ArrowLeft, FileSpreadsheet, Download, Edit, Eye, X, 
  Copy, AlertTriangle, Check, Trash2, FileText, Package, ClipboardList, ChevronDown, ChevronUp, Calendar, Filter, CheckCircle, FileDown,
  Settings, Upload, Loader2
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import M45NavPills from '../components/M45NavPills';
import OpsReviewModal from '../components/OpsReviewModal';

// Import utilities and constants
import {
  formatDateOrdinal,
  formatDateDDMMYY,
  formatCurrency,
  sortByPONumber,
  calculateTotals,
  calculatePL,
  groupExpensesByMonth,
  filterExpensesByDate,
  toLocalDateString,
  getCurrentMonthRange
} from '../utils/poExpenseHelpers';

import {
  enconOrange,
  enconBlack,
  enconGrey,
  TRANSFER_BRANCHES,
  PO_TYPES
} from '../constants/poExpenseConstants';

const API = process.env.REACT_APP_BACKEND_URL;

const POExpenses = ({ user }) => {
  const navigate = useNavigate();
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const poActivityIdRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);
  
  // Date filter state - always reset to current month on navigation
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  // Reset to current month when component mounts or route changes
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setFilterStartDate(toLocalDateString(start));
    setFilterEndDate(toLocalDateString(end));
  }, []);
  const [filterExpanded, setFilterExpanded] = useState(false); // Filter bar collapsed by default
  const [departmentFilter, setDepartmentFilter] = useState('all'); // Department filter

  // Whether the user has explicitly narrowed the date range. The "current
  // month / last month / older months (collapsed)" grouping below only makes
  // sense for the default unfiltered view — when a specific date filter is
  // active we must show every matching month, otherwise an explicitly
  // requested older range (e.g. "3 months ago") gets hidden behind the
  // "Show Older Months" toggle and looks like there are no expenses at all.
  const hasActiveDateFilter = !!(filterStartDate || filterEndDate);
  
  // Get current month name for display
  const getCurrentMonthName = () => {
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    if (filterStartDate) {
      const date = new Date(filterStartDate + 'T12:00:00');
      return months[date.getMonth()];
    }
    return months[new Date().getMonth()];
  };
  
  // Invoice matching state
  const [matchInvoiceMode, setMatchInvoiceMode] = useState(false);
  const [queryNotes, setQueryNotes] = useState('');
  
  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [stockTransferModalOpen, setStockTransferModalOpen] = useState(false);
  const [pendingTransferData, setPendingTransferData] = useState(null);
  
  // Transfer linking state
  const [showTransferOptions, setShowTransferOptions] = useState(false);
  const [showLinkTransferModal, setShowLinkTransferModal] = useState(false);
  const [showAllTransfersModal, setShowAllTransfersModal] = useState(false);
  const [recentTransfersForLink, setRecentTransfersForLink] = useState([]);
  const [allTransfersForLink, setAllTransfersForLink] = useState([]);
  const [viewingLinkedTransfer, setViewingLinkedTransfer] = useState(null);
  const [showLinkedTransferModal, setShowLinkedTransferModal] = useState(false);
  
  // Edit Transfer Modal State
  const [editingTransfer, setEditingTransfer] = useState(false);
  const [editTransferData, setEditTransferData] = useState(null);
  
  // Tools dropdown and BI EXT Transport modal state
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [biExtTransportModalOpen, setBiExtTransportModalOpen] = useState(false);
  const [savingTransferEdit, setSavingTransferEdit] = useState(false);
  
  // State for month expansion in All P.O.s section
  const [showOlderMonths, setShowOlderMonths] = useState(false);
  
  // State for category breakdown dropdown in summary bar
  const [showCategoryBreakdown, setShowCategoryBreakdown] = useState(false);
  
  // Create Transfer Modal State (nested modal)
  const [showCreateTransferModal, setShowCreateTransferModal] = useState(false);
  const [newTransferData, setNewTransferData] = useState({
    branch_id: '',
    branch_name: '',
    branch_address: '',
    branch_contact: '',
    branch_phone: '',
    requested_by: '',
    collection_date: '',
    notes: '',
    items: [{ id: Date.now(), product_code: '', description: '', quantity: 1, stock_unit: 'EACH' }]
  });
  const [savingTransfer, setSavingTransfer] = useState(false);
  
  // Form state - simplified
  const [formData, setFormData] = useState({
    supplier: '',
    order_date: toLocalDateString(new Date()),
    ordered_by: '',
    customer_name: '',
    delivery_postcode: '',
    m4_order_no: '',
    notes: '',
    user_notes: '', // The user-entered portion of notes
    message_to_supplier: '',
    total_cost: 0,
    customer_recharge_amount: 0,
    ho_recharge_amount: 0,
    po_type: '',
    linked_contribution: 0,
    linked_transfer_id: '',
    linked_transfer_ref: '',
  });
  
  // State for two-step modal - track if PO type has been selected
  const [poTypeSelected, setPoTypeSelected] = useState(false);
  
  // Additional customers state for multi-customer P.O.s
  const [additionalCustomers, setAdditionalCustomers] = useState([]);
  
  // Get display name for "Ordered By" field
  // Priority: full_name > username > email prefix (e.g., "j.moseley" from "j.moseley@encon.co.uk")
  const getOrderedByName = () => {
    if (!user) return '';
    // Try full_name first
    if (user.full_name && user.full_name.trim()) {
      return user.full_name.trim();
    }
    // Try username next
    if (user.username && user.username.trim()) {
      return user.username.trim();
    }
    // Fall back to email prefix
    if (user.email) {
      return user.email.split('@')[0];
    }
    return '';
  };
  
  // Helper to auto-capitalize text input
  const handleUppercaseChange = (field, value) => {
    setFormData({ ...formData, [field]: value.toUpperCase() });
  };
  
  const [nextPONumber, setNextPONumber] = useState('');
  const [customPONumber, setCustomPONumber] = useState(''); // For super admin override
  const [contributionData, setContributionData] = useState(null);
  const [reservationId, setReservationId] = useState(null); // Track reserved P.O. number
  
  // Quick links state
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);
  
  // Load quick links
  const loadQuickLinks = async () => {
    try {
      const response = await api.get(`${API}/api/quick-links`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setQuickLinks(response.data);
    } catch (error) {
      console.error('Error loading quick links:', error);
    }
  };

  const loadLinkGroups = async () => {
    try {
      const response = await api.get(`${API}/api/quick-link-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLinkGroups(response.data);
    } catch (error) {
      console.error('Error loading link groups:', error);
    }
  };

  const getLinksForGroup = (groupId) => {
    return quickLinks.filter(link => (link.group_id || 'default') === groupId).sort((a, b) => a.order - b.order);
  };

  const handleQuickLinkClick = (link) => {
    if (link.url) {
      window.open(link.url, '_blank');
    }
  };

  // Load transfers for linking
  const loadRecentTransfersForLinking = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const response = await api.get(`${API}/api/stock-transfers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to current month only
      const monthTransfers = (response.data.transfers || []).filter(t => {
        const tDate = new Date(t.created_at);
        return tDate.getMonth() + 1 === currentMonth && tDate.getFullYear() === currentYear;
      });
      setRecentTransfersForLink(monthTransfers.slice(0, 20));
    } catch (error) {
      console.error('Error loading transfers for linking:', error);
    }
  };
  
  const loadAllTransfersForLinking = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/api/stock-transfers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllTransfersForLink(response.data.transfers || []);
    } catch (error) {
      console.error('Error loading all transfers:', error);
    }
  };
  
  const handleLinkTransfer = (transfer) => {
    setFormData({
      ...formData,
      linked_transfer_id: transfer.id,
      linked_transfer_ref: transfer.branch_name,
    });
    setShowLinkTransferModal(false);
    setShowAllTransfersModal(false);
    toast.success(`Linked to transfer: ${transfer.branch_name}`);
  };
  
  // Create a new transfer from nested modal and link it
  const handleSaveNewTransfer = async () => {
    if (!newTransferData.branch_id) {
      toast.error('Please select a branch');
      return;
    }
    if (!newTransferData.requested_by.trim()) {
      toast.error('Please enter your name');
      return;
    }
    const validItems = newTransferData.items.filter(item => 
      item.product_code.trim() && item.quantity > 0
    );
    if (validItems.length === 0) {
      toast.error('Please add at least one product');
      return;
    }
    
    setSavingTransfer(true);
    try {
      const token = localStorage.getItem('token');
      const branch = TRANSFER_BRANCHES.find(b => b.id === newTransferData.branch_id);
      
      const transferPayload = {
        branch_id: branch.id,
        branch_name: branch.name,
        branch_address: `${branch.address1}, ${branch.address2}, ${branch.city}, ${branch.postcode}`,
        branch_contact: branch.contact || '',
        branch_phone: branch.phone || '',
        requested_by: newTransferData.requested_by,
        collection_date: newTransferData.collection_date || null,
        notes: newTransferData.notes,
        items: validItems.map(item => ({
          product_code: item.product_code,
          description: item.description,
          quantity_required: parseInt(item.quantity) || 1,
          stock_unit: item.stock_unit || 'EACH'
        })),
        linked_po_id: null,
        linked_po_number: null
      };
      
      const response = await api.post(`${API}/api/stock-transfers`, transferPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const savedTransfer = response.data.transfer;
      
      // Link to this P.O. form
      setFormData({
        ...formData,
        linked_transfer_id: savedTransfer.id,
        linked_transfer_ref: savedTransfer.branch_name,
      });
      
      // Reset and close transfer modal
      setNewTransferData({
        branch_id: '',
        branch_name: '',
        branch_address: '',
        branch_contact: '',
        branch_phone: '',
        requested_by: '',
        collection_date: '',
        notes: '',
        items: [{ id: Date.now(), product_code: '', description: '', quantity: 1, stock_unit: 'EACH' }]
      });
      setShowCreateTransferModal(false);
      toast.success(`Transfer created and linked: ${savedTransfer.branch_name}`);
      
    } catch (error) {
      console.error('Error creating transfer:', error);
      toast.error('Failed to create transfer');
    } finally {
      setSavingTransfer(false);
    }
  };
  
  // Add item to new transfer
  const addTransferItem = () => {
    setNewTransferData({
      ...newTransferData,
      items: [...newTransferData.items, { id: Date.now(), product_code: '', description: '', quantity: 1, stock_unit: 'EACH' }]
    });
  };
  
  // Remove item from new transfer
  const removeTransferItem = (index) => {
    if (newTransferData.items.length > 1) {
      setNewTransferData({
        ...newTransferData,
        items: newTransferData.items.filter((_, i) => i !== index)
      });
    }
  };
  
  // Update transfer item
  const updateTransferItem = (index, field, value) => {
    const newItems = [...newTransferData.items];
    newItems[index][field] = value;
    setNewTransferData({ ...newTransferData, items: newItems });
  };
  
  // Fetch and view a linked transfer
  const handleViewLinkedTransfer = async (transferId, linkedPONumber = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/api/stock-transfers/${transferId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        // Add the linked P.O. number to the transfer data for display
        setViewingLinkedTransfer({
          ...response.data,
          displayLinkedPONumber: linkedPONumber || response.data.linked_po_number
        });
        setEditingTransfer(false);
        setEditTransferData(null);
        setShowLinkedTransferModal(true);
      } else {
        toast.error('Linked transfer no longer exists');
      }
    } catch (error) {
      console.error('Error loading transfer:', error);
      if (error.response?.status === 404) {
        toast.error('Linked transfer was deleted or no longer exists');
      } else {
        toast.error('Failed to load transfer details');
      }
    }
  };
  
  // Start editing the transfer
  const handleStartEditTransfer = () => {
    if (viewingLinkedTransfer) {
      setEditTransferData({
        requested_by: viewingLinkedTransfer.requested_by || '',
        collection_date: viewingLinkedTransfer.collection_date || '',
        notes: viewingLinkedTransfer.notes || '',
        items: viewingLinkedTransfer.items?.map(item => ({
          ...item,
          id: item.id || Date.now() + Math.random()
        })) || [{ id: Date.now(), product_code: '', description: '', quantity_required: 1, stock_unit: 'EACH' }]
      });
      setEditingTransfer(true);
    }
  };
  
  // Save transfer edits
  const handleSaveTransferEdit = async () => {
    if (!viewingLinkedTransfer || !editTransferData) return;
    
    setSavingTransferEdit(true);
    try {
      const token = localStorage.getItem('token');
      
      const updatePayload = {
        branch_id: viewingLinkedTransfer.branch_id,
        branch_name: viewingLinkedTransfer.branch_name,
        branch_address: viewingLinkedTransfer.branch_address,
        branch_contact: viewingLinkedTransfer.branch_contact || '',
        branch_phone: viewingLinkedTransfer.branch_phone || '',
        requested_by: editTransferData.requested_by,
        collection_date: editTransferData.collection_date || null,
        notes: editTransferData.notes,
        items: editTransferData.items.filter(item => item.product_code?.trim()).map(item => ({
          product_code: item.product_code,
          description: item.description || '',
          quantity_required: parseInt(item.quantity_required) || 1,
          stock_unit: item.stock_unit || 'EACH'
        })),
        linked_po_id: viewingLinkedTransfer.linked_po_id || null,
        linked_po_number: viewingLinkedTransfer.linked_po_number || null
      };
      
      await api.put(`${API}/api/stock-transfers/${viewingLinkedTransfer.id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Transfer updated successfully');
      
      // Refresh the transfer data
      const refreshResponse = await api.get(`${API}/api/stock-transfers/${viewingLinkedTransfer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewingLinkedTransfer({
        ...refreshResponse.data,
        displayLinkedPONumber: viewingLinkedTransfer.displayLinkedPONumber
      });
      setEditingTransfer(false);
      setEditTransferData(null);
      
    } catch (error) {
      console.error('Error updating transfer:', error);
      toast.error('Failed to update transfer');
    } finally {
      setSavingTransferEdit(false);
    }
  };
  
  // Add item to edit transfer
  const addEditTransferItem = () => {
    if (editTransferData) {
      setEditTransferData({
        ...editTransferData,
        items: [...editTransferData.items, { id: Date.now(), product_code: '', description: '', quantity_required: 1, stock_unit: 'EACH' }]
      });
    }
  };
  
  // Remove item from edit transfer
  const removeEditTransferItem = (index) => {
    if (editTransferData && editTransferData.items.length > 1) {
      setEditTransferData({
        ...editTransferData,
        items: editTransferData.items.filter((_, i) => i !== index)
      });
    }
  };
  
  // Update edit transfer item
  const updateEditTransferItem = (index, field, value) => {
    if (editTransferData) {
      const newItems = [...editTransferData.items];
      newItems[index][field] = value;
      setEditTransferData({ ...editTransferData, items: newItems });
    }
  };
  
  // Open the linked P.O. for editing from the transfer view modal
  const handleEditLinkedPOFromTransfer = () => {
    if (viewingLinkedTransfer?.linked_po_id) {
      // Find the P.O. in our expenses list
      const linkedPO = expenses.find(e => e.id === viewingLinkedTransfer.linked_po_id);
      if (linkedPO) {
        setShowLinkedTransferModal(false);
        setViewingExpense(linkedPO);
        setFormData({
          supplier: linkedPO.supplier || '',
          order_date: linkedPO.order_date || toLocalDateString(new Date()),
          ordered_by: linkedPO.ordered_by || '',
          m4_order_no: linkedPO.m4_order_no || '',
          notes: linkedPO.notes || '',
          total_cost: linkedPO.total_cost || 0,
          customer_recharge_amount: linkedPO.customer_recharge_amount || 0,
          ho_recharge_amount: linkedPO.ho_recharge_amount || 0,
          po_type: linkedPO.po_type || 'branch_expense',
          linked_transfer_id: linkedPO.linked_transfer_id || '',
          linked_transfer_ref: linkedPO.linked_transfer_ref || '',
        });
        setEditMode(true);
        setAddModalOpen(true);
      } else {
        toast.error('Could not find linked P.O.');
      }
    }
  };
  
  const loadExpenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const [allRes, recentRes] = await Promise.all([
        api.get(`${API}/api/po-expenses`, { headers: { Authorization: `Bearer ${token}` } }),
        api.get(`${API}/api/po-expenses/recent?limit=6`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setExpenses(allRes.data.expenses || []);
      setRecentExpenses(recentRes.data.expenses || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error('Failed to load P.O. expenses');
      setLoading(false);
    }
  };
  
  const loadNextPONumber = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/api/po-expenses/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNextPONumber(response.data.next_po_number);
    } catch (error) {
      console.error('Error loading next P.O. number:', error);
    }
  };
  
  useEffect(() => {
    const fetchData = async () => {
      await loadExpenses();
      await loadNextPONumber();
      await loadQuickLinks();
      await loadLinkGroups();
    };
    fetchData();
     
  }, []);
  
  const checkContribution = async (orderNumber) => {
    if (!orderNumber) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/api/po-expenses/check-contribution/${orderNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.found) {
        setContributionData(response.data);
        setFormData(prev => ({
          ...prev,
          linked_contribution: response.data.contribution
        }));
        toast.success(`Found contribution: £${formatCurrency(response.data.contribution)}`);
      } else {
        setContributionData(null);
      }
    } catch (error) {
      console.error('Error checking contribution:', error);
    }
  };
  
  const openAddModal = async () => {
    setFormData({
      supplier: '',
      order_date: toLocalDateString(new Date()),
      ordered_by: getOrderedByName(),
      customer_name: '',
      delivery_postcode: '',
      m4_order_no: '',
      notes: '',
      user_notes: '',
      message_to_supplier: '',
      total_cost: 0,
      customer_recharge_amount: 0,
      ho_recharge_amount: 0,
      po_type: '',
      linked_contribution: 0,
      linked_transfer_id: '',
      linked_transfer_ref: '',
      department: '',
      expense_type: 'sale',
    });
    setContributionData(null);
    setCustomPONumber(''); // Reset custom P.O. number
    setAdditionalCustomers([]); // Reset additional customers
    setPoTypeSelected(false); // Reset two-step modal state
    setEditMode(false);
    
    // Reserve a P.O. number to prevent race conditions
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`${API}/api/po-expenses/reserve-number`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNextPONumber(response.data.po_number);
      setReservationId(response.data.reservation_id);
      console.log('Reserved P.O. number:', response.data.po_number);
    } catch (error) {
      console.error('Error reserving P.O. number:', error);
      toast.error('Failed to reserve P.O. number');
    }
    
    setAddModalOpen(true);

    // Modal UI v2 — register as a resumable "In Progress" activity for new P.O.s.
    if (modalUiV2) {
      poActivityIdRef.current = `po-expense-${Date.now()}`;
      activityBar.openActivity({
        type: 'po_expense',
        activityId: poActivityIdRef.current,
        title: 'New P.O. Expense',
        subtitle: 'P.O. Expense',
        snapshot: { activityId: poActivityIdRef.current },
      });
    }
  };
  
  // Release reservation when modal is closed without saving
  const handleCloseAddModal = async () => {
    if (modalUiV2 && poActivityIdRef.current && !editMode) {
      activityBar.minimizeActive();
      poActivityIdRef.current = null;
    }
    if (reservationId && !editMode) {
      try {
        const token = localStorage.getItem('token');
        await api.delete(`${API}/api/po-expenses/release-reservation/${reservationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Released P.O. reservation:', reservationId);
      } catch (error) {
        console.error('Error releasing reservation:', error);
      }
    }
    setReservationId(null);
    setAddModalOpen(false);
  };

  // Keep the live "In Progress" snapshot fresh as the new P.O. is filled in.
  useEffect(() => {
    if (!addModalOpen || editMode || !modalUiV2 || !poActivityIdRef.current) return;
    activityBar.updateActiveSnapshot(
      { activityId: poActivityIdRef.current },
      { title: `PO - ${formData.supplier || formData.customer_name || 'New Expense'}`, subtitle: 'P.O. Expense' }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addModalOpen, editMode, modalUiV2, formData.supplier, formData.customer_name]);
  
  const openViewModal = (expense) => {
    setViewingExpense(expense);
    setViewModalOpen(true);
  };
  
  const openEditModal = (expense) => {
    // Extract user notes from the full notes if it has the PO# prefix
    let userNotes = expense.notes || '';
    const poPrefix = `${expense.po_number} - ${expense.customer_name || ''} - `.trim();
    if (userNotes.startsWith(poPrefix)) {
      userNotes = userNotes.substring(poPrefix.length).trim();
    }
    
    setFormData({
      supplier: expense.supplier || '',
      order_date: expense.order_date || '',
      ordered_by: expense.ordered_by || '',
      customer_name: expense.customer_name || '',
      delivery_postcode: expense.delivery_postcode || '',
      m4_order_no: expense.m4_order_no || '',
      notes: expense.notes || '',
      user_notes: userNotes,
      message_to_supplier: expense.message_to_supplier || '',
      total_cost: expense.total_cost || 0,
      customer_recharge_amount: expense.customer_recharge_amount || 0,
      ho_recharge_amount: expense.ho_recharge_amount || 0,
      po_type: expense.po_type || 'branch_expense',
      linked_contribution: expense.linked_contribution || 0,
      linked_transfer_id: expense.linked_transfer_id || '',
      linked_transfer_ref: expense.linked_transfer_ref || '',
      department: expense.department || '',
      expense_type: expense.expense_type || 'sale',
    });
    // Load additional customers if present
    setAdditionalCustomers(expense.additional_customers || []);
    setViewingExpense(expense);
    setPoTypeSelected(true); // In edit mode, always show full form
    setEditMode(true);
    setViewModalOpen(false);
    setAddModalOpen(true);
  };
  
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { qty: '', unit: '', description: '', rate: '', total: 0 }]
    }));
  };
  
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      const newTotal = newItems.reduce((sum, item) => sum + (item.total || 0), 0);
      setFormData(prev => ({
        ...prev,
        items: newItems,
        total_cost: newTotal
      }));
    }
  };
  
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Calculate line total
    if (field === 'qty' || field === 'rate') {
      const qty = parseFloat(newItems[index].qty) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      newItems[index].total = qty * rate;
    }
    
    // Update total cost
    const newTotal = newItems.reduce((sum, item) => sum + (item.total || 0), 0);
    
    setFormData(prev => ({
      ...prev,
      items: newItems,
      total_cost: newTotal
    }));
  };
  
  const handleSave = async () => {
    if (!formData.department) {
      toast.error('Please select a department (Structural, Industrial, or Other)');
      return;
    }
    // Validate notes field is required
    if (!formData.user_notes || !formData.user_notes.trim()) {
      toast.error('Notes field is required');
      return;
    }
    // Validate M4 order number is required when expense_type is "sale"
    if (formData.expense_type === 'sale' && (!formData.m4_order_no || !formData.m4_order_no.trim())) {
      toast.error('M4 Order Number is required for Sales type');
      return;
    }
    await saveExpense();
  };

  const saveExpense = async () => {
    try {
      const token = localStorage.getItem('token');
      let savedPO = null;
      
      // Build the full notes string with PO number and customer name prepended
      const poNumber = editMode ? viewingExpense?.po_number : (customPONumber || nextPONumber);
      const customerName = formData.customer_name || '';
      const userNotes = formData.user_notes || '';
      
      // Format: "PO123 - Customer Name - User notes" or "PO123 - User notes" if no customer
      let fullNotes = poNumber;
      if (customerName.trim()) {
        fullNotes += ` - ${customerName.trim()}`;
      }
      fullNotes += ` - ${userNotes.trim()}`;
      
      const dataToSave = {
        ...formData,
        notes: fullNotes // This is the combined notes for storage and Excel export
      };
      
      if (editMode && viewingExpense) {
        // Update existing - include additional customers
        const updateData = {
          ...dataToSave,
          additional_customers: additionalCustomers.filter(c => c.customer_name || c.m4_order_no || c.delivery_postcode)
        };
        const response = await api.put(`${API}/api/po-expenses/${viewingExpense.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedPO = response.data;
        toast.success('P.O. updated successfully');
      } else {
        // Create new - include custom P.O. number if super admin provided one
        // Also include reservation_id to use the reserved number and additional customers
        const createData = {
          ...dataToSave,
          reservation_id: reservationId,
          additional_customers: additionalCustomers.filter(c => c.customer_name || c.m4_order_no || c.delivery_postcode),
          ...(customPONumber && customPONumber !== nextPONumber && user?.role === 'super_admin' 
            ? { custom_po_number: customPONumber } 
            : {})
        };
        
        const response = await api.post(`${API}/api/po-expenses`, createData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedPO = response.data;
        setReservationId(null); // Clear reservation after successful save
        
        // Copy P.O. number to clipboard (may fail if not in secure context)
        const poNumberSaved = response.data.po_number;
        try {
          await navigator.clipboard.writeText(poNumberSaved);
          toast.success(`P.O. ${poNumberSaved} created and copied to clipboard!`);
        } catch (clipboardError) {
          console.log('Clipboard not available, P.O. created:', poNumberSaved);
          toast.success(`P.O. ${poNumberSaved} created successfully!`);
        }

        // Completed — moves from "In Progress" into "Recents".
        if (modalUiV2 && poActivityIdRef.current) {
          activityBar.logRecentView({
            type: 'po_expense',
            activityId: poActivityIdRef.current,
            title: `PO - ${poNumberSaved}`,
            subtitle: 'Completed',
            snapshot: { activityId: poActivityIdRef.current },
          });
          activityBar.discardActive();
          poActivityIdRef.current = null;
        }
      }
      
      setAddModalOpen(false);
      setWarningModalOpen(false);
      setCustomPONumber(''); // Reset custom P.O. number
      setAdditionalCustomers([]); // Reset additional customers
      setPoTypeSelected(false); // Reset two-step modal state
      
      // If transfer toggle is on, open stock transfer modal
      if (formData.create_transfer && savedPO) {
        setPendingTransferData({
          supplier: formData.supplier,
          po_number: savedPO.po_number || viewingExpense?.po_number,
          branch: 'Birmingham'
        });
        setStockTransferModalOpen(true);
      }
      
      loadExpenses();
      loadNextPONumber();
    } catch (error) {
      console.error('Error saving P.O.:', error);
      toast.error('Failed to save P.O. expense');
    }
  };
  
  const handleCancel = async (expense) => {
    if (!window.confirm('Mark this P.O. as cancelled? The number will show as unused.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/api/po-expenses/${expense.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('P.O. marked as cancelled');
      setViewModalOpen(false);
      loadExpenses();
    } catch (error) {
      console.error('Error cancelling P.O.:', error);
      toast.error('Failed to cancel P.O.');
    }
  };
  
  const handleExcelDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/api/po-expenses/excel/generate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert base64 to blob and download
      const binaryString = atob(response.data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Failed to generate Excel file');
    }
  };
  
  const copyPONumber = async (poNumber) => {
    try {
      await navigator.clipboard.writeText(poNumber);
      toast.success(`${poNumber} copied to clipboard`);
    } catch (error) {
      console.log('Clipboard not available');
      toast.info(`P.O. Number: ${poNumber}`);
    }
  };
  
  // Generate PDF for Purchase Order
  const generatePDF = async (expense) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`${API}/api/po-expenses/${expense.id}/generate-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO_${expense.po_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };
  
  // Toggle Match Invoice mode (extends the view modal)
  const toggleMatchInvoiceMode = () => {
    setQueryNotes(viewingExpense?.query_notes || '');
    setMatchInvoiceMode(!matchInvoiceMode);
  };
  
  // Mark expense as matched
  const markAsMatched = async () => {
    if (!viewingExpense) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/api/po-expenses/${viewingExpense.id}`, {
        invoice_matched: true,
        matched_at: new Date().toISOString(),
        query_notes: queryNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Invoice marked as matched');
      setMatchInvoiceMode(false);
      setQueryNotes('');
      setViewModalOpen(false);
      loadExpenses();
    } catch (error) {
      console.error('Error marking as matched:', error);
      toast.error('Failed to mark as matched');
    }
  };
  
  // Save query notes without marking as matched
  const saveQueryNotes = async () => {
    if (!viewingExpense) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/api/po-expenses/${viewingExpense.id}`, {
        query_notes: queryNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Query notes saved');
      setMatchInvoiceMode(false);
      setQueryNotes('');
      setViewModalOpen(false);
      loadExpenses();
    } catch (error) {
      console.error('Error saving query notes:', error);
      toast.error('Failed to save query notes');
    }
  };
  
  // Un-cancel a P.O.
  const handleUncancel = async (expense) => {
    if (!window.confirm('Restore this cancelled P.O.?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/api/po-expenses/${expense.id}`, {
        status: 'active'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('P.O. restored successfully');
      setViewModalOpen(false);
      loadExpenses();
    } catch (error) {
      console.error('Error restoring P.O.:', error);
      toast.error('Failed to restore P.O.');
    }
  };
  
  // Header buttons for P.O. Expenses page - View Excel, Add P.O.
  const headerRightButtons = (
    <>
      {/* View Excel Button - White bg, gradient green border, rounded, gradient green text */}
      <button
        onClick={() => setExcelModalOpen(true)}
        className="px-4 py-2 rounded-xl font-semibold flex items-center gap-2 transition-all hover:shadow-md relative"
        style={{ 
          backgroundColor: 'white',
          border: '2px solid transparent',
          backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #90EE90, #228B22)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        }}
      >
        <FileSpreadsheet className="w-4 h-4" style={{ color: '#228B22' }} />
        <span className="hidden sm:inline bg-gradient-to-r from-green-400 to-green-700 bg-clip-text text-transparent">
          View Excel
        </span>
      </button>
      
      {/* Add P.O. Button - White bg, orange border, rounded, orange text */}
      <button
        onClick={openAddModal}
        className="px-4 py-2 rounded-xl font-semibold flex items-center gap-2 transition-all hover:shadow-md"
        style={{ 
          backgroundColor: 'white', 
          border: `2px solid ${enconOrange}`,
          color: enconOrange
        }}
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">Add P.O.</span>
      </button>
    </>
  );

  // Mobile menu buttons
  const mobileMenuButtons = [
    {
      icon: FileSpreadsheet,
      label: 'View Excel',
      onClick: () => setExcelModalOpen(true),
      color: '#228B22',
      textColor: '#228B22'
    },
    {
      icon: Plus,
      label: 'Add P.O.',
      onClick: openAddModal,
      color: enconOrange,
      textColor: enconOrange
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Global Header — no page-specific buttons (moved into page toolbar) */}
      <AppHeader 
        user={user} 
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">P.O. Expenses</span>
          </div>
        }
      />
      
      {/* Main Content */}
      <div className="pt-[20px] px-4 md:px-6 pb-6 bg-gray-100 min-h-screen">
        <div className="max-w-[1800px] mx-auto">
          {/* Nav pills — now in AppHeader global header */}
          
          {/* Date Filter Bar + Add P.O. + Excel + Tools Button */}
          <div className="mb-4 relative h-12 flex justify-between items-center gap-3">
            {/* Filter Bar Container - slides to match totals bar width */}
            <div 
              className={`h-full flex items-center bg-white border shadow-sm transition-all duration-300 ease-in-out overflow-hidden ${
                filterExpanded ? 'flex-1 rounded-xl' : 'w-auto rounded-xl'
              }`}
            >
              {/* Filter Toggle Button - Always visible */}
              <Button
                onClick={() => setFilterExpanded(!filterExpanded)}
                className="h-full px-4 flex items-center gap-2 transition-all duration-200 shrink-0 bg-gray-600 text-white hover:bg-gray-700 rounded-l-xl rounded-r-none min-w-[180px]"
              >
                <Filter className="w-4 h-4" />
                <span className="font-medium">Filter by Date</span>
                {(filterStartDate || filterEndDate) && (
                  <span className="ml-1 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {filterExpensesByDate(expenses, filterStartDate, filterEndDate).length}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${filterExpanded ? '-rotate-90' : 'rotate-0'}`} />
              </Button>
              
              {/* Current View Indicator - Always visible on the white part */}
              <div className={`flex flex-col items-center justify-center px-4 py-1 bg-white border-l transition-all duration-300 ${
                filterExpanded ? 'min-w-[70px]' : 'min-w-[70px]'
              }`}>
                <span className="text-[9px] text-gray-400 uppercase tracking-wide leading-tight">Current View</span>
                <span className="text-sm font-bold" style={{ color: enconOrange }}>{getCurrentMonthName()}</span>
              </div>
              
              {/* Sliding Content - appears when expanded */}
              <div 
                className={`flex items-center gap-4 px-4 flex-1 transition-all duration-300 ease-in-out ${
                  filterExpanded 
                    ? 'opacity-100' 
                    : 'opacity-0 max-w-0 overflow-hidden'
                }`}
              >
                {/* Divider */}
                <div className="h-6 w-px bg-gray-300"></div>
                
                {/* Quick filter buttons - moved before date inputs */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const range = getCurrentMonthRange();
                    setFilterStartDate(range.start);
                    setFilterEndDate(range.end);
                  }}
                  className="text-xs whitespace-nowrap h-8"
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  This Month
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilterStartDate('');
                    setFilterEndDate('');
                  }}
                  className="text-xs whitespace-nowrap h-8"
                >
                  All Dates
                </Button>
                
                {/* Divider */}
                <div className="h-6 w-px bg-gray-300"></div>
                
                {/* Date Inputs - moved further right */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 whitespace-nowrap font-medium">From:</label>
                  <Input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    className="h-8 text-sm w-[140px]"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 whitespace-nowrap font-medium">To:</label>
                  <Input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    className="h-8 text-sm w-[140px]"
                  />
                </div>
                
                {/* Department Filter + Show filtered count */}
                <div className="flex items-center gap-3 ml-auto">
                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    className="h-8 text-xs border rounded-lg px-2 bg-white focus:ring-2 focus:ring-orange-500"
                    data-testid="department-filter"
                  >
                    <option value="all">All Departments</option>
                    <option value="structural">Structural</option>
                    <option value="industrial">Industrial</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* View Excel Button */}
            <button
              onClick={() => setExcelModalOpen(true)}
              className="h-12 px-4 rounded-xl font-semibold flex items-center gap-2 transition-all hover:shadow-md relative shrink-0"
              style={{ 
                backgroundColor: 'white',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #90EE90, #228B22)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
              }}
              data-testid="po-view-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4" style={{ color: '#228B22' }} />
              <span className="hidden sm:inline bg-gradient-to-r from-green-400 to-green-700 bg-clip-text text-transparent font-semibold">View Excel</span>
            </button>
            
            {/* Add P.O. Button */}
            <button
              onClick={openAddModal}
              className="h-12 px-4 rounded-xl font-semibold flex items-center gap-2 transition-all hover:shadow-md shrink-0"
              style={{ 
                backgroundColor: 'white', 
                border: `2px solid ${enconOrange}`,
                color: enconOrange
              }}
              data-testid="po-add-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add P.O.</span>
            </button>

            {/* Tools Button - Right aligned */}
            <div className="relative">
              <Button
                onClick={() => setToolsDropdownOpen(!toolsDropdownOpen)}
                className="h-12 px-4 flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white rounded-xl"
              >
                <Settings className="w-4 h-4" />
                <span className="font-medium">Tools</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${toolsDropdownOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {/* Tools Dropdown */}
              {toolsDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl border shadow-lg z-50 min-w-[200px] overflow-hidden">
                  <button
                    onClick={() => {
                      setBiExtTransportModalOpen(true);
                      setToolsDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-gray-700">BI EXT Transport</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Totals Summary Bar - Uses filtered expenses */}
          {expenses.length > 0 && (
            <div className="mb-6 bg-gray-600 rounded-xl p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Total Expenses */}
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Total Expenses</div>
                  <div className="text-lg font-bold" style={{ color: enconOrange }}>
                    £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalExpenses)}
                  </div>
                </div>
                
                {/* Total Ex Transport */}
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Total Ex Transport</div>
                  <div className="text-lg font-bold" style={{ color: enconOrange }}>
                    £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalExTransport)}
                  </div>
                </div>
                
                {/* Total Recharges */}
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Total Recharges</div>
                  <div className="text-lg font-bold text-blue-600">
                    £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalRecharges)}
                  </div>
                </div>
                
                {/* Total H/O Recharge */}
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Total H/O Recharge</div>
                  <div className="text-lg font-bold text-purple-600">
                    £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalHORecharge)}
                  </div>
                </div>
                
                {/* Total Ex Transport Cost (Net) */}
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Ex Transport Net</div>
                  <div className="text-lg font-bold text-green-600">
                    £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalExTransportNet)}
                  </div>
                </div>
              </div>
              
              {/* Dropdown toggle button */}
              <div className="flex justify-center mt-2">
                <button
                  onClick={() => setShowCategoryBreakdown(!showCategoryBreakdown)}
                  className="flex items-center gap-1 text-white text-xs hover:bg-gray-500 px-3 py-1 rounded transition-colors"
                >
                  <span>{showCategoryBreakdown ? 'Hide' : 'Show'} Category Breakdown</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryBreakdown ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              {/* Category Breakdown - Hidden by default */}
              {showCategoryBreakdown && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                  {/* Staff */}
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">Staff</div>
                    <div className="text-sm font-semibold text-gray-700">
                      £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalStaff)}
                    </div>
                  </div>
                  
                  {/* CV Hire */}
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">CV Hire</div>
                    <div className="text-sm font-semibold text-gray-700">
                      £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalCVHire)}
                    </div>
                  </div>
                  
                  {/* Maintenance */}
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">Maintenance</div>
                    <div className="text-sm font-semibold text-gray-700">
                      £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalMaintenance)}
                    </div>
                  </div>
                  
                  {/* Stationary */}
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">Stationary</div>
                    <div className="text-sm font-semibold text-gray-700">
                      £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalStationary)}
                    </div>
                  </div>
                  
                  {/* Other */}
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">Other</div>
                    <div className="text-sm font-semibold text-gray-700">
                      £{formatCurrency(calculateTotals(filterExpensesByDate(expenses, filterStartDate, filterEndDate)).totalOther)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Recent P.O.s Section - Show 6 cards */}
          {recentExpenses.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent P.O.s</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentExpenses.slice(0, 6).map((expense) => (
                  <div key={expense.id} className="flex gap-2 h-[140px]">
                    {/* Main P.O. Card - Fixed height, no expansion */}
                    <Card
                      className={`bg-white cursor-pointer hover:shadow-lg transition-shadow border-2 ${expense.linked_transfer_id ? 'flex-[4]' : 'flex-1'} overflow-hidden`}
                      style={{ borderColor: expense.status === 'cancelled' ? '#ef4444' : expense.invoice_matched ? '#22c55e' : enconOrange }}
                      onClick={() => openViewModal(expense)}
                    >
                      <CardContent className="p-3 h-full flex flex-col">
                        {/* Row 1: P.O. number, ordered by, date, status badges */}
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <span className="text-lg font-bold flex-shrink-0" style={{ color: enconOrange }}>
                              {expense.po_number}
                            </span>
                            {expense.ordered_by && (
                              <span className="bg-gray-200 text-gray-600 text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0">
                                {expense.ordered_by}
                              </span>
                            )}
                            {expense.invoice_matched && (
                              <span className="bg-green-100 text-green-700 text-xs font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0">
                                <CheckCircle className="w-3 h-3" />
                                Matched
                              </span>
                            )}
                            {expense.status === 'cancelled' && (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded flex-shrink-0">Cancelled</span>
                            )}
                          </div>
                          {/* Date on the right */}
                          <span className="text-xs text-gray-500 flex-shrink-0">{formatDateDDMMYY(expense.order_date)}</span>
                        </div>
                        
                        {/* Row 2: Supplier and cost */}
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="text-sm text-gray-700 truncate font-medium flex-1">{expense.supplier}</div>
                          <div className="text-sm font-semibold flex-shrink-0" style={{ color: enconOrange }}>
                            £{formatCurrency(expense.total_cost || 0)}
                          </div>
                        </div>
                        
                        {/* Row 3: Customer/postcode and recharge info */}
                        {((expense.customer_name || expense.delivery_postcode) || ((expense.customer_recharge_amount || 0) + (expense.ho_recharge_amount || 0)) > 0) && (
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="text-xs text-gray-500 truncate flex-1">
                              {expense.customer_name}{expense.customer_name && expense.delivery_postcode ? ' • ' : ''}{expense.delivery_postcode}
                              {expense.additional_customers?.length > 0 && (
                                <span className="ml-1 text-xs bg-gray-200 text-gray-600 px-1 py-0.5 rounded">
                                  +{expense.additional_customers.length}
                                </span>
                              )}
                            </div>
                            {((expense.customer_recharge_amount || 0) + (expense.ho_recharge_amount || 0)) > 0 && (
                              <div className="flex items-center gap-1 text-xs flex-shrink-0">
                                <span className="text-blue-600">-£{formatCurrency((expense.customer_recharge_amount || 0) + (expense.ho_recharge_amount || 0))}</span>
                                <span className="text-gray-400">=</span>
                                <span className="font-bold text-green-600">£{formatCurrency(calculatePL(expense))}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Row 4: Notes on second line - truncated to fit */}
                        {expense.notes && (
                          <div className="text-xs text-gray-400 truncate mt-auto pt-1 border-t border-gray-100">
                            {expense.notes}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Linked Transfer Mini Card - Blue */}
                    {expense.linked_transfer_id && (
                      <Card 
                        className="flex-1 hover:shadow-lg transition-shadow cursor-pointer border-2 border-blue-400 bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewLinkedTransfer(expense.linked_transfer_id, expense.po_number);
                        }}
                      >
                        <CardContent className="p-2 flex flex-col items-center justify-center h-full">
                          <Package className="w-6 h-6 mb-1 text-blue-600" />
                          <span className="text-xs font-bold text-blue-600 text-center leading-tight">
                            {expense.linked_transfer_ref || 'Transfer'}
                          </span>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* All P.O.s - Grouped by Month */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              All P.O.s ({(() => {
                let filtered = filterExpensesByDate(expenses, filterStartDate, filterEndDate);
                if (departmentFilter !== 'all') {
                  filtered = filtered.filter(e => e.department === departmentFilter);
                }
                return filtered.length;
              })()})
            </h2>
            
            {expenses.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500">No P.O. expenses yet</p>
                  <Button
                    onClick={openAddModal}
                    className="mt-4"
                    style={{ backgroundColor: enconOrange }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First P.O.
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {(() => {
                  // Apply BOTH department AND date filters
                  let displayExpenses = filterExpensesByDate(expenses, filterStartDate, filterEndDate);
                  
                  // Count expenses with undefined/null department
                  const unassignedCount = displayExpenses.filter(e => !e.department).length;
                  
                  if (departmentFilter !== 'all') {
                    // When filtering by department, ONLY show items with that department
                    // Exclude undefined/null departments from filtered view
                    displayExpenses = displayExpenses.filter(e => e.department === departmentFilter);
                  }
                  
                  // Show warning if there are unassigned departments when filter is active
                  const showUnassignedWarning = departmentFilter !== 'all' && unassignedCount > 0;
                  
                  return (
                    <>
                      {showUnassignedWarning && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                          <div className="text-yellow-600">⚠️</div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-yellow-800">
                              {unassignedCount} P.O.{unassignedCount !== 1 ? 's have' : ' has'} no department assigned
                            </p>
                            <p className="text-xs text-yellow-700 mt-1">
                              Select "All Departments" to see unassigned P.O.s, then edit them to assign a department (Structural/Industrial/Other).
                            </p>
                          </div>
                        </div>
                      )}
                      {groupExpensesByMonth(displayExpenses).map((monthGroup, groupIndex) => {
                  // Show current month and last month in detail, older months collapsed by default
                  const shouldShowDetailed = monthGroup.isCurrentMonth || monthGroup.isLastMonth;
                  const isOlderMonth = monthGroup.isOlder;
                  
                  // Don't render older months unless expanded — UNLESS the user has an
                  // active date filter, in which case always show every matching month.
                  if (!hasActiveDateFilter && isOlderMonth && !showOlderMonths && groupIndex > 1) {
                    // Show expand button only after the last visible month
                    if (groupIndex === 2) {
                      const olderMonthsCount = groupExpensesByMonth(expenses).filter(g => g.isOlder).length;
                      return (
                        <div key="expand-older" className="flex justify-center">
                          <Button
                            variant="outline"
                            onClick={() => setShowOlderMonths(true)}
                            className="flex items-center gap-2 border-gray-300 text-gray-600 hover:bg-gray-100"
                          >
                            <ChevronDown className="w-4 h-4" />
                            Show {olderMonthsCount} Older Month{olderMonthsCount > 1 ? 's' : ''}
                          </Button>
                        </div>
                      );
                    }
                    return null;
                  }
                  
                  return (
                    <div key={monthGroup.key} className="bg-white rounded-xl border p-4">
                      {/* Month Title */}
                      <h3 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span style={{ color: enconOrange }}>{monthGroup.name}</span>
                        <span className="text-sm text-gray-400">({monthGroup.expenses.length} P.O.s)</span>
                      </h3>
                      
                      {/* P.O. Cards Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {monthGroup.expenses.map((expense) => (
                          <Card
                            key={expense.id}
                            className={`cursor-pointer hover:shadow-md transition-shadow h-[140px] flex flex-col border-2 ${expense.status === 'cancelled' ? 'opacity-50' : ''}`}
                            style={{ 
                              backgroundColor: expense.invoice_matched ? '#f0fdf4' : '#f9fafb',
                              borderColor: expense.status === 'cancelled' ? '#ef4444' : expense.invoice_matched ? '#22c55e' : 'transparent'
                            }}
                            onClick={() => openViewModal(expense)}
                          >
                            <CardContent className="p-3 flex-1 flex flex-col">
                              <div className="flex justify-between flex-1">
                                {/* Left side - P.O. details */}
                                <div className="flex-1 pr-2 min-w-0">
                                  {/* P.O. number and initials */}
                                  <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                                    <span className="font-bold text-sm" style={{ color: enconOrange }}>
                                      {expense.po_number}
                                    </span>
                                    {expense.ordered_by && (
                                      <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-1 py-0.5 rounded">
                                        {expense.ordered_by}
                                      </span>
                                    )}
                                    {expense.invoice_matched && (
                                      <span className="bg-green-100 text-green-700 text-[10px] font-bold px-1 py-0.5 rounded flex items-center gap-0.5">
                                        <CheckCircle className="w-2.5 h-2.5" />
                                        Matched
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-600 truncate">{expense.supplier || 'No supplier'}</div>
                                  {(expense.customer_name || expense.delivery_postcode) && (
                                    <div className="text-[10px] text-gray-500 truncate">
                                      {expense.customer_name}{expense.customer_name && expense.delivery_postcode ? ' • ' : ''}{expense.delivery_postcode}
                                      {expense.additional_customers?.length > 0 && (
                                        <span className="ml-1 bg-gray-200 text-gray-600 px-1 rounded">
                                          +{expense.additional_customers.length}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <div className="text-[10px] text-gray-500">{formatDateDDMMYY(expense.order_date)}</div>
                                  {(expense.department || expense.expense_type) && (
                                    <div className="flex gap-1 mt-0.5 flex-wrap">
                                      {expense.department && (
                                        <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${
                                          expense.department === 'structural' ? 'bg-blue-100 text-blue-700' :
                                          expense.department === 'industrial' ? 'bg-purple-100 text-purple-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>{expense.department.charAt(0).toUpperCase() + expense.department.slice(1)}</span>
                                      )}
                                      {expense.expense_type && expense.expense_type !== 'sale' && (
                                        <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${
                                          expense.expense_type === 'error' ? 'bg-red-100 text-red-700' :
                                          expense.expense_type === 'transfer' ? 'bg-teal-100 text-teal-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>{expense.expense_type.charAt(0).toUpperCase() + expense.expense_type.slice(1)}</span>
                                      )}
                                    </div>
                                  )}
                                  {expense.status === 'cancelled' && (
                                    <span className="text-[10px] text-red-500">Cancelled</span>
                                  )}
                                </div>
                                
                                {/* Right side - Cost/Recharge/P&L */}
                                <div className="flex flex-col items-end justify-center border-l pl-2 min-w-[70px]">
                                  <div className="text-xs font-semibold" style={{ color: enconOrange }}>
                                    £{formatCurrency(expense.total_cost || 0, 0)}
                                  </div>
                                  {((expense.customer_recharge_amount || 0) + (expense.ho_recharge_amount || 0)) > 0 && (
                                    <>
                                      <div className="text-[10px] text-blue-600">
                                        -£{formatCurrency((expense.customer_recharge_amount || 0) + (expense.ho_recharge_amount || 0), 0)}
                                      </div>
                                      <div className="border-t border-gray-300 w-full my-0.5"></div>
                                      <div className="text-xs font-bold text-green-600">
                                        £{formatCurrency(calculatePL(expense), 0)}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* Linked Transfer - Slim oblong at bottom inside card */}
                              {expense.linked_transfer_id && (
                                <div 
                                  className="mt-2 bg-blue-50 border border-blue-300 rounded-full px-3 py-1 flex items-center justify-center gap-1.5 cursor-pointer hover:bg-blue-100 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewLinkedTransfer(expense.linked_transfer_id, expense.po_number);
                                  }}
                                >
                                  <Package className="w-3 h-3 text-blue-600" />
                                  <span className="text-[10px] font-semibold text-blue-600">
                                    {expense.linked_transfer_ref || 'Linked Transfer'}
                                  </span>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
                </>
                );
                })()}
                
                {/* Collapse button when older months are shown */}
                {!hasActiveDateFilter && showOlderMonths && groupExpensesByMonth(departmentFilter === 'all' ? expenses : expenses.filter(e => e.department === departmentFilter)).some(g => g.isOlder) && (
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      onClick={() => setShowOlderMonths(false)}
                      className="flex items-center gap-2 border-gray-300 text-gray-600 hover:bg-gray-100"
                    >
                      <ChevronUp className="w-4 h-4" />
                      Hide Older Months
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Add/Edit P.O. Modal */}
      <Dialog open={addModalOpen} onOpenChange={(open) => {
        if (!open) {
          handleCloseAddModal();
        }
      }}>
        <DialogContent 
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          {...(modalUiV2 ? { topOffset: activityBar.headerHeight, overlayClassName: 'bg-black/50' } : {})}
          style={{
            background: 'white',
            borderRadius: '16px',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ color: enconOrange }}>
              {editMode ? (
                `Edit P.O. - ${viewingExpense?.po_number}`
              ) : (
                <div className="flex items-center gap-2">
                  <span>New P.O. -</span>
                  {user?.role === 'super_admin' ? (
                    <input
                      type="text"
                      value={customPONumber || nextPONumber}
                      onChange={(e) => setCustomPONumber(e.target.value.toUpperCase())}
                      className="font-bold text-xl border-b-2 border-dashed border-orange-300 bg-transparent focus:outline-none focus:border-orange-500 w-32 text-center"
                      style={{ color: enconOrange }}
                      title="Super Admin: Click to edit P.O. number"
                    />
                  ) : (
                    <span className="font-bold">{nextPONumber}</span>
                  )}
                  {user?.role === 'super_admin' && customPONumber && customPONumber !== nextPONumber && (
                    <span className="text-xs text-gray-500 font-normal">(custom)</span>
                  )}
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Step 1: P.O. Type selector - always shown first */}
            <div>
              <Label className="text-base font-semibold">P.O. Type <span className="text-red-500">*</span></Label>
              <select
                className="w-full p-3 border rounded-lg text-base mt-1"
                value={formData.po_type}
                onChange={(e) => {
                  setFormData({ ...formData, po_type: e.target.value });
                  if (e.target.value) {
                    setPoTypeSelected(true);
                  }
                }}
                style={{ borderColor: enconOrange }}
              >
                <option value="">Please select the correct expense</option>
                {PO_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {!poTypeSelected && !editMode && (
                <p className="text-xs text-gray-500 mt-1">Select a P.O. type to continue adding expense details</p>
              )}
            </div>
            
            {/* Step 2: Rest of the form - only shown after PO type is selected */}
            {(poTypeSelected || editMode) && (
            <>
            {/* Branch Info with Ordered By */}
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">Ordering Branch: Encon Insulation Birmingham</div>
                  <div className="text-gray-600">21 Gavin Way, Nexus Point, Birmingham, B6 7AF</div>
                </div>
                <div className="text-right">
                  <Label className="text-xs text-gray-500">Ordered By</Label>
                  <Input
                    value={formData.ordered_by}
                    onChange={(e) => handleUppercaseChange('ordered_by', e.target.value)}
                    placeholder="Initials"
                    className="w-20 text-center font-semibold mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Department & Type Selection */}
            <div className="grid grid-cols-2 gap-4 border-b pb-3">
              <div>
                <Label className="text-xs font-semibold text-gray-500 uppercase mb-1.5 block">Department <span className="text-red-500">*</span></Label>
                <div className="flex gap-3">
                  {['Structural', 'Industrial', 'Other'].map(dept => (
                    <label key={dept} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="department"
                        checked={formData.department === dept.toLowerCase()}
                        onChange={() => setFormData({ ...formData, department: dept.toLowerCase() })}
                        className="accent-orange-500"
                      />
                      <span className="text-sm">{dept}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-500 uppercase mb-1.5 block">Type</Label>
                <div className="flex gap-3">
                  {['Sale', 'Error', 'Transfer', 'Other'].map(t => (
                    <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="expense_type"
                        checked={formData.expense_type === t.toLowerCase()}
                        onChange={() => setFormData({ ...formData, expense_type: t.toLowerCase() })}
                        className="accent-orange-500"
                      />
                      <span className="text-sm">{t}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Main Form Grid - Row 1: Supplier & Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier</Label>
                <Input
                  value={formData.supplier}
                  onChange={(e) => handleUppercaseChange('supplier', e.target.value)}
                  placeholder="Supplier name"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.order_date}
                  onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                />
              </div>
            </div>
            
            {/* Row 2: Customer Name, M4 Order No, Delivery Postcode */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Customer Name</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => handleUppercaseChange('customer_name', e.target.value)}
                  placeholder="Customer name"
                />
              </div>
              <div>
                <Label>M4 ORDER NO</Label>
                <Input
                  value={formData.m4_order_no}
                  onChange={(e) => handleUppercaseChange('m4_order_no', e.target.value)}
                  onBlur={(e) => checkContribution(e.target.value)}
                  placeholder="Order number"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label>Delivery Postcode</Label>
                  <Input
                    value={formData.delivery_postcode}
                    onChange={(e) => handleUppercaseChange('delivery_postcode', e.target.value)}
                    placeholder="Postcode"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAdditionalCustomers([...additionalCustomers, { id: Date.now(), customer_name: '', m4_order_no: '', delivery_postcode: '' }])}
                    className="h-10 px-3 text-xs whitespace-nowrap border-2"
                    style={{ borderColor: enconOrange, color: enconOrange }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Additional Customers Section */}
            {additionalCustomers.length > 0 && (
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg border">
                <div className="text-xs font-semibold text-gray-500 uppercase">Additional Customers</div>
                {additionalCustomers.map((cust, index) => (
                  <div key={cust.id} className="grid grid-cols-4 gap-3 items-end">
                    <div>
                      <Label className="text-xs">Customer {index + 2}</Label>
                      <Input
                        value={cust.customer_name}
                        onChange={(e) => {
                          const updated = [...additionalCustomers];
                          updated[index].customer_name = e.target.value.toUpperCase();
                          setAdditionalCustomers(updated);
                        }}
                        placeholder="Customer name"
                        className="h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">M4 Order No</Label>
                      <Input
                        value={cust.m4_order_no}
                        onChange={(e) => {
                          const updated = [...additionalCustomers];
                          updated[index].m4_order_no = e.target.value.toUpperCase();
                          setAdditionalCustomers(updated);
                        }}
                        placeholder="Order number"
                        className="h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Postcode</Label>
                      <Input
                        value={cust.delivery_postcode}
                        onChange={(e) => {
                          const updated = [...additionalCustomers];
                          updated[index].delivery_postcode = e.target.value.toUpperCase();
                          setAdditionalCustomers(updated);
                        }}
                        placeholder="Postcode"
                        className="h-9 text-sm"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAdditionalCustomers(additionalCustomers.filter(c => c.id !== cust.id))}
                      className="h-9 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Contribution display */}
            {contributionData && (
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <div className="text-xs text-green-600">Linked Contribution Found</div>
                <div className="font-semibold text-green-700 text-lg">£{formatCurrency(contributionData.contribution)}</div>
              </div>
            )}
            
            {/* Cost Boxes - 3 main inputs */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-orange-50 p-4 rounded-lg border-2" style={{ borderColor: enconOrange }}>
                <Label className="text-sm font-semibold" style={{ color: enconOrange }}>Cost (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.total_cost || ''}
                  onChange={(e) => setFormData({ ...formData, total_cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="text-xl font-bold mt-1"
                  style={{ borderColor: enconOrange }}
                />
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-400">
                <Label className="text-sm font-semibold text-blue-600">Customer Recharge (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.customer_recharge_amount || ''}
                  onChange={(e) => setFormData({ ...formData, customer_recharge_amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="text-xl font-bold mt-1 border-blue-400"
                />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-400">
                <Label className="text-sm font-semibold text-purple-600">H/O Recharge (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.ho_recharge_amount || ''}
                  onChange={(e) => setFormData({ ...formData, ho_recharge_amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="text-xl font-bold mt-1 border-purple-400"
                />
              </div>
            </div>
            
            {/* Notes - REQUIRED */}
            <div>
              <Label>Notes <span className="text-red-500">*</span></Label>
              <div className="text-xs text-gray-500 mb-1">
                Your notes will be saved as: <span className="font-semibold" style={{ color: enconOrange }}>
                  {editMode ? viewingExpense?.po_number : (customPONumber || nextPONumber)} - {formData.customer_name || '[Customer Name]'} - [Your notes]
                </span>
              </div>
              <textarea
                className={`w-full p-3 border rounded-lg resize-none ${!formData.user_notes?.trim() ? 'border-red-300' : ''}`}
                rows={3}
                value={formData.user_notes}
                onChange={(e) => setFormData({ ...formData, user_notes: e.target.value })}
                placeholder="Enter notes (required) - This will appear with PO number and customer name in Excel"
                required
              />
              {!formData.user_notes?.trim() && (
                <p className="text-xs text-red-500 mt-1">Notes are required</p>
              )}
            </div>
            
            {/* Message to Supplier */}
            <div>
              <Label className="flex items-center gap-2">
                <span>Message to Supplier</span>
                <span className="text-xs text-gray-400 font-normal">(appears on PDF)</span>
              </Label>
              <textarea
                className="w-full p-3 border rounded-lg resize-none border-orange-200 focus:border-orange-400"
                rows={2}
                value={formData.message_to_supplier}
                onChange={(e) => setFormData({ ...formData, message_to_supplier: e.target.value })}
                placeholder="Special instructions or message for the supplier..."
              />
            </div>
            
            {/* Transfer Link Section */}
            <div className="border-t pt-4">
              {formData.linked_transfer_id ? (
                // Show linked transfer
                <div 
                  className="p-3 rounded-lg border-2 border-blue-400 bg-blue-50 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => window.location.href = `/stock-transfer?highlight=${formData.linked_transfer_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-bold text-blue-600">Linked Transfer</p>
                        <p className="text-sm text-gray-600">{formData.linked_transfer_ref || 'View transfer'}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">Click to view</span>
                  </div>
                </div>
              ) : null}
            </div>
            </>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                handleCloseAddModal();
                setPoTypeSelected(false);
              }}
              className="rounded-xl px-6"
            >
              Cancel
            </Button>
            
            <div className="flex items-center gap-3">
              {/* Add Transfer Link - Only show when not already linked and form is visible */}
              {(poTypeSelected || editMode) && !formData.linked_transfer_id && (
                <button
                  type="button"
                  onClick={() => setShowTransferOptions(true)}
                  className="px-4 py-2 rounded-xl border-2 hover:bg-blue-50 transition-all flex items-center gap-2"
                  style={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                >
                  <Package className="w-4 h-4" />
                  <span className="font-semibold text-sm">Add / Link A Stock Transfer Collection Request</span>
                </button>
              )}
              
              <Button 
                onClick={handleSave} 
                className="rounded-xl px-6 border-2 hover:bg-orange-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: 'white', 
                  borderColor: enconOrange,
                  color: enconOrange
                }}
                disabled={!poTypeSelected && !editMode}
              >
                {editMode ? 'Update P.O.' : 'Create P.O.'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Transfer Options Modal */}
      <Dialog open={showTransferOptions} onOpenChange={setShowTransferOptions}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Package className="w-5 h-5" />
              Link Stock Transfer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {/* Create New Transfer */}
            <button
              onClick={() => {
                setShowTransferOptions(false);
                // Open nested create transfer modal instead of navigating
                setNewTransferData({
                  branch_id: '',
                  branch_name: '',
                  branch_address: '',
                  branch_contact: '',
                  branch_phone: '',
                  requested_by: formData.ordered_by || '',
                  collection_date: '',
                  notes: formData.supplier ? `P.O. for ${formData.supplier}` : '',
                  items: [{ id: Date.now(), product_code: '', description: '', quantity: 1, stock_unit: 'EACH' }]
                });
                setShowCreateTransferModal(true);
              }}
              className="w-full p-4 rounded-lg border-2 border-blue-400 bg-blue-50 hover:bg-blue-100 transition-all text-left"
            >
              <div className="font-semibold text-blue-600">Create New Transfer</div>
              <p className="text-sm text-gray-600">Create a new transfer and link it to this P.O.</p>
            </button>
            
            {/* Link to Current Month Transfers */}
            <button
              onClick={() => {
                setShowTransferOptions(false);
                setShowLinkTransferModal(true);
                loadRecentTransfersForLinking();
              }}
              className="w-full p-4 rounded-lg border-2 border-gray-300 hover:border-blue-400 hover:bg-gray-50 transition-all text-left"
            >
              <div className="font-semibold text-gray-700">Link to Current Month Transfer</div>
              <p className="text-sm text-gray-600">Select from this month&apos;s transfers</p>
            </button>
            
            {/* Link to Any Transfer */}
            <button
              onClick={() => {
                setShowTransferOptions(false);
                setShowAllTransfersModal(true);
                loadAllTransfersForLinking();
              }}
              className="w-full p-4 rounded-lg border-2 border-gray-300 hover:border-blue-400 hover:bg-gray-50 transition-all text-left"
            >
              <div className="font-semibold text-gray-700">Link to Any Transfer</div>
              <p className="text-sm text-gray-600">Browse all transfers regardless of date</p>
            </button>
          </div>
          
          <Button variant="outline" onClick={() => setShowTransferOptions(false)} className="w-full">
            Cancel
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Link to Recent Transfer Modal */}
      <Dialog open={showLinkTransferModal} onOpenChange={setShowLinkTransferModal}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-blue-600">Select Transfer (Current Month)</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2 py-2 max-h-[50vh] overflow-y-auto">
            {recentTransfersForLink.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No transfers found this month</p>
            ) : (
              recentTransfersForLink.map(transfer => (
                <button
                  key={transfer.id}
                  onClick={() => handleLinkTransfer(transfer)}
                  className="w-full p-3 rounded-lg border-2 hover:border-blue-400 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="font-semibold">{transfer.branch_name}</div>
                  <p className="text-sm text-gray-600">
                    {new Date(transfer.created_at).toLocaleDateString('en-GB')} • {transfer.items?.length || 0} items • By: {transfer.requested_by}
                  </p>
                </button>
              ))
            )}
          </div>
          
          <Button variant="outline" onClick={() => setShowLinkTransferModal(false)} className="w-full">
            Cancel
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Link to All Transfers Modal */}
      <Dialog open={showAllTransfersModal} onOpenChange={setShowAllTransfersModal}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-blue-600">Select Transfer (All)</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2 py-2 max-h-[50vh] overflow-y-auto">
            {allTransfersForLink.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No transfers found</p>
            ) : (
              allTransfersForLink.map(transfer => (
                <button
                  key={transfer.id}
                  onClick={() => handleLinkTransfer(transfer)}
                  className="w-full p-3 rounded-lg border-2 hover:border-blue-400 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="font-semibold">{transfer.branch_name}</div>
                  <p className="text-sm text-gray-600">
                    {new Date(transfer.created_at).toLocaleDateString('en-GB')} • {transfer.items?.length || 0} items • By: {transfer.requested_by}
                  </p>
                </button>
              ))
            )}
          </div>
          
          <Button variant="outline" onClick={() => setShowAllTransfersModal(false)} className="w-full">
            Cancel
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* View P.O. Modal with gradient border and rounded corners */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent 
          className="max-w-2xl rounded-2xl overflow-y-auto"
          {...(modalUiV2 ? { topOffset: activityBar.headerHeight, overlayClassName: 'bg-black/50' } : {})}
          style={{
            background: 'white',
            border: '3px solid',
            borderImage: 'linear-gradient(to right, #ee7203, #ec4899) 1',
          }}
          data-testid="view-po-modal"
        >
          {viewingExpense && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold flex items-center gap-3" style={{ color: enconOrange }}>
                  {viewingExpense.po_number}
                  {viewingExpense.status === 'cancelled' && (
                    <span className="text-sm px-2 py-1 bg-red-100 text-red-600 rounded">Cancelled</span>
                  )}
                  {viewingExpense.invoice_matched && (
                    <span className="text-sm px-2 py-1 bg-green-100 text-green-600 rounded flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Matched
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Summary Info */}
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <div className="text-xs text-gray-500">Supplier</div>
                    <div className="font-medium">{viewingExpense.supplier || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Date</div>
                    <div className="font-medium">{formatDateDDMMYY(viewingExpense.order_date)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Ordered By</div>
                    <div className="font-medium">{viewingExpense.ordered_by || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Type</div>
                    <div className="font-medium">
                      {PO_TYPES.find(t => t.value === viewingExpense.po_type)?.label || viewingExpense.po_type}
                    </div>
                  </div>
                  {viewingExpense.customer_name && (
                    <div>
                      <div className="text-xs text-gray-500">Customer Name</div>
                      <div className="font-medium">{viewingExpense.customer_name}</div>
                    </div>
                  )}
                  {viewingExpense.delivery_postcode && (
                    <div>
                      <div className="text-xs text-gray-500">Delivery Postcode</div>
                      <div className="font-medium">{viewingExpense.delivery_postcode}</div>
                    </div>
                  )}
                  {viewingExpense.m4_order_no && (
                    <div>
                      <div className="text-xs text-gray-500">M4 Order No</div>
                      <div className="font-medium">{viewingExpense.m4_order_no}</div>
                    </div>
                  )}
                  {viewingExpense.linked_contribution > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">Linked Contribution</div>
                      <div className="font-medium text-green-600">£{formatCurrency(viewingExpense.linked_contribution)}</div>
                    </div>
                  )}
                </div>
                
                {/* Additional Customers Display */}
                {viewingExpense.additional_customers && viewingExpense.additional_customers.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg border">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Additional Customers</div>
                    <div className="space-y-2">
                      {viewingExpense.additional_customers.map((cust, index) => (
                        <div key={index} className="flex items-center gap-4 text-sm bg-white p-2 rounded border">
                          <span className="font-medium text-gray-600">#{index + 2}</span>
                          {cust.customer_name && <span className="font-medium">{cust.customer_name}</span>}
                          {cust.m4_order_no && <span className="text-gray-500">Order: {cust.m4_order_no}</span>}
                          {cust.delivery_postcode && <span className="text-gray-500">{cust.delivery_postcode}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Cost Boxes */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-orange-50 p-4 rounded-lg border-2 text-center" style={{ borderColor: enconOrange }}>
                    <div className="text-xs text-gray-500">Cost</div>
                    <div className="font-bold text-2xl" style={{ color: enconOrange }}>
                      £{formatCurrency(viewingExpense.total_cost || 0)}
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-400 text-center">
                    <div className="text-xs text-gray-500">Customer Recharge</div>
                    <div className="font-bold text-2xl text-blue-600">
                      £{formatCurrency(viewingExpense.customer_recharge_amount || 0)}
                    </div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-400 text-center">
                    <div className="text-xs text-gray-500">H/O Recharge</div>
                    <div className="font-bold text-2xl text-purple-600">
                      £{formatCurrency(viewingExpense.ho_recharge_amount || 0)}
                    </div>
                  </div>
                </div>
                
                {/* Notes */}
                {viewingExpense.notes && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Notes</div>
                    <div className="text-sm">{viewingExpense.notes}</div>
                  </div>
                )}
                
                {/* Message to Supplier */}
                {viewingExpense.message_to_supplier && (
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <div className="text-xs font-medium mb-1" style={{ color: enconOrange }}>Message to Supplier</div>
                    <div className="text-sm text-gray-700">{viewingExpense.message_to_supplier}</div>
                  </div>
                )}
                
                {/* Query Notes (existing) */}
                {viewingExpense.query_notes && !matchInvoiceMode && (
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <div className="text-xs text-yellow-700 mb-1">Query Notes</div>
                    <div className="text-sm text-yellow-800">{viewingExpense.query_notes}</div>
                  </div>
                )}
                
                {/* Match Invoice Extended Section */}
                {matchInvoiceMode && (
                  <div className="border-t pt-4 mt-4 space-y-4 bg-green-50 -mx-6 px-6 pb-4 -mb-4">
                    <div className="flex items-center gap-2 text-green-700 font-semibold">
                      <CheckCircle className="w-5 h-5" />
                      Match Invoice
                    </div>
                    
                    {/* Query Notes Input */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Query Notes</Label>
                      <Textarea
                        value={queryNotes}
                        onChange={(e) => setQueryNotes(e.target.value)}
                        placeholder="Add any query notes or discrepancies here..."
                        className="mt-1 min-h-[80px] bg-white"
                      />
                    </div>
                    
                    {/* Match Actions */}
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setMatchInvoiceMode(false);
                          setQueryNotes('');
                        }}
                        className="text-gray-600"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="outline"
                        onClick={saveQueryNotes}
                        className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                      >
                        Save Notes Only
                      </Button>
                      <Button
                        onClick={markAsMatched}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Mark as Matched
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Actions - Hide when in match mode */}
              {!matchInvoiceMode && (
                <div className="flex justify-between items-center pt-4 border-t mt-4">
                  <div className="flex gap-2">
                    {viewingExpense.status === 'cancelled' ? (
                      <Button
                        variant="outline"
                        onClick={() => handleUncancel(viewingExpense)}
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        data-testid="po-view-restore-button"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Restore P.O.
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => handleCancel(viewingExpense)}
                        className="text-red-500 border-red-300 hover:bg-red-50"
                        data-testid="po-view-cancel-button"
                      >
                        Cancel P.O.
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Button
                      variant="outline"
                      onClick={() => generatePDF(viewingExpense)}
                      className="text-gray-700 border-gray-300 hover:bg-gray-50"
                      data-testid="po-view-create-pdf-button"
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Create PDF
                    </Button>
                    <Button
                      onClick={toggleMatchInvoiceMode}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="po-view-match-invoice-button"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Match Invoice
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => copyPONumber(viewingExpense.po_number)}
                      style={{ borderColor: enconOrange, color: enconOrange }}
                      data-testid="po-view-copy-button"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => openEditModal(viewingExpense)}
                      style={{ borderColor: enconOrange, color: enconOrange }}
                      data-testid="po-view-edit-button"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Excel Modal */}
      <Dialog open={excelModalOpen} onOpenChange={setExcelModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: enconOrange }}>
              <FileSpreadsheet className="w-5 h-5" />
              Expenses Excel
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-gray-600">
              Generate the expenses spreadsheet matching the 2025 Expenses template. This includes all P.O.s organized by month with formulas for totals.
            </p>
            
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleExcelDownload}
                className="w-full flex items-center justify-center gap-2"
                style={{ backgroundColor: enconOrange }}
              >
                <Download className="w-4 h-4" />
                Download Excel
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setExcelModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Stock Transfer Modal - Opens after P.O. saved with transfer toggle */}
      <Dialog open={stockTransferModalOpen} onOpenChange={setStockTransferModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: enconOrange }}>
              <FileText className="w-5 h-5" />
              Create Stock Transfer
            </DialogTitle>
          </DialogHeader>
          
          {pendingTransferData && (
            <div className="py-4 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Transfer details from P.O.:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">P.O. Number:</span>
                    <span className="ml-2 font-semibold">{pendingTransferData.po_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Supplier:</span>
                    <span className="ml-2 font-semibold">{pendingTransferData.supplier}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Branch:</span>
                    <span className="ml-2 font-semibold">{pendingTransferData.branch}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm">
                Click below to open the Stock Transfer page with these details pre-filled in the notes.
              </p>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setStockTransferModalOpen(false);
                    setPendingTransferData(null);
                  }}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    const supplier = encodeURIComponent(pendingTransferData.supplier || '');
                    const poNumber = encodeURIComponent(pendingTransferData.po_number || '');
                    navigate(`/stock-transfer?from_po=true&supplier=${supplier}&po_number=${poNumber}`);
                    setStockTransferModalOpen(false);
                    setPendingTransferData(null);
                  }}
                  className="flex-1"
                  style={{ backgroundColor: enconOrange }}
                >
                  Open Stock Transfer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* View Linked Transfer Modal */}
      <Dialog open={showLinkedTransferModal} onOpenChange={(open) => {
        if (!open) {
          setEditingTransfer(false);
          setEditTransferData(null);
        }
        setShowLinkedTransferModal(open);
      }}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" style={{ borderRadius: '24px' }}>
          {viewingLinkedTransfer && (
            <>
              {/* P.O. Number Badge - Top Center, Clickable */}
              {viewingLinkedTransfer.displayLinkedPONumber && (
                <div className="flex justify-center -mt-2 mb-2">
                  <button
                    onClick={handleEditLinkedPOFromTransfer}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border-2 hover:shadow-lg transition-all hover:scale-105"
                    style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}
                  >
                    <ClipboardList className="w-5 h-5" style={{ color: enconOrange }} />
                    <span className="font-bold text-base" style={{ color: enconOrange }}>
                      {viewingLinkedTransfer.displayLinkedPONumber}
                    </span>
                  </button>
                </div>
              )}
              
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2 text-blue-600">
                  <Package className="h-6 w-6" />
                  {editingTransfer ? 'Edit Transfer' : 'Linked Transfer'}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Branch Info */}
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                  <p className="font-bold text-blue-900 text-lg">{viewingLinkedTransfer.branch_name}</p>
                  <p className="text-sm text-gray-600">{viewingLinkedTransfer.branch_address}</p>
                  {!editingTransfer && (
                    <>
                      <p className="text-sm text-gray-600 mt-2">
                        <strong>Requested By:</strong> {viewingLinkedTransfer.requested_by}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Date:</strong> {new Date(viewingLinkedTransfer.created_at).toLocaleDateString('en-GB')}
                      </p>
                      {viewingLinkedTransfer.collection_date && (
                        <p className="text-sm text-gray-600">
                          <strong>Collection:</strong> {new Date(viewingLinkedTransfer.collection_date).toLocaleDateString('en-GB')}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* View Mode - Items Table */}
                {!editingTransfer && (
                  <div>
                    <h3 className="font-semibold mb-2">Products ({viewingLinkedTransfer.items?.length || 0})</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2">Code</th>
                            <th className="text-left p-2">Description</th>
                            <th className="text-right p-2">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(viewingLinkedTransfer.items || []).map((item, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-2 font-mono text-xs">{item.product_code}</td>
                              <td className="p-2">{item.description}</td>
                              <td className="p-2 text-right font-semibold">{item.quantity_required} {item.stock_unit}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Notes - show P.O. number at start if linked */}
                {!editingTransfer && (viewingLinkedTransfer.notes || viewingLinkedTransfer.displayLinkedPONumber) && (
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <p className="text-sm text-gray-600">
                      <strong>Notes:</strong>{' '}
                      {viewingLinkedTransfer.displayLinkedPONumber && (
                        <span className="font-semibold" style={{ color: enconOrange }}>
                          {viewingLinkedTransfer.displayLinkedPONumber}
                        </span>
                      )}
                      {viewingLinkedTransfer.displayLinkedPONumber && viewingLinkedTransfer.notes && ' - '}
                      {viewingLinkedTransfer.notes}
                    </p>
                  </div>
                )}

                {/* Edit Mode Form */}
                {editingTransfer && editTransferData && (
                  <div className="space-y-4 border-t pt-4">
                    <div>
                      <Label>Requested By</Label>
                      <Input
                        value={editTransferData.requested_by}
                        onChange={(e) => setEditTransferData({...editTransferData, requested_by: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Collection Date</Label>
                      <Input
                        type="date"
                        value={editTransferData.collection_date || ''}
                        onChange={(e) => setEditTransferData({...editTransferData, collection_date: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Products</Label>
                      <div className="space-y-2 mt-1">
                        {editTransferData.items.map((item, index) => (
                          <div key={item.id || index} className="flex items-center gap-2">
                            <Input
                              value={item.product_code}
                              onChange={(e) => updateEditTransferItem(index, 'product_code', e.target.value)}
                              placeholder="Code"
                              className="w-24 font-mono text-sm"
                            />
                            <Input
                              value={item.description || ''}
                              onChange={(e) => updateEditTransferItem(index, 'description', e.target.value)}
                              placeholder="Description"
                              className="flex-1 text-sm"
                            />
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity_required}
                              onChange={(e) => updateEditTransferItem(index, 'quantity_required', e.target.value)}
                              className="w-16 text-center"
                            />
                            <button
                              type="button"
                              onClick={addEditTransferItem}
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white bg-blue-500"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                            {editTransferData.items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeEditTransferItem(index)}
                                className="w-7 h-7 rounded-full flex items-center justify-center text-white bg-red-500"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <textarea
                        value={editTransferData.notes}
                        onChange={(e) => setEditTransferData({...editTransferData, notes: e.target.value})}
                        placeholder="Additional notes..."
                        className="mt-1 w-full p-3 border rounded-xl resize-none h-16"
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-white pb-2">
                  {!editingTransfer ? (
                    <>
                      <Button
                        variant="outline"
                        className="flex-1 py-4 rounded-xl border-2"
                        onClick={() => {
                          setShowLinkedTransferModal(false);
                          setViewingLinkedTransfer(null);
                        }}
                      >
                        Close
                      </Button>
                      <Button
                        className="flex-1 py-4 rounded-xl text-white font-semibold"
                        style={{ backgroundColor: '#3b82f6' }}
                        onClick={handleStartEditTransfer}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Transfer
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        className="flex-1 py-4 rounded-xl border-2"
                        onClick={() => {
                          setEditingTransfer(false);
                          setEditTransferData(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 py-4 rounded-xl text-white font-semibold"
                        style={{ backgroundColor: '#3b82f6' }}
                        onClick={handleSaveTransferEdit}
                        disabled={savingTransferEdit}
                      >
                        {savingTransferEdit ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Create Transfer Modal (Nested - overlays on P.O. modal) */}
      <Dialog open={showCreateTransferModal} onOpenChange={setShowCreateTransferModal}>
        <DialogContent 
          className="max-w-2xl bg-white z-[60] max-h-[90vh] overflow-y-auto"
          style={{ 
            borderRadius: '24px',
            border: '3px solid #3b82f6',
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-blue-600">
              <Package className="w-5 h-5" />
              Create New Transfer
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Branch Selection */}
            {!newTransferData.branch_id ? (
              <div>
                <Label className="text-base font-semibold mb-3 block">Select Branch</Label>
                <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                  {TRANSFER_BRANCHES.map(branch => (
                    <button
                      key={branch.id}
                      type="button"
                      className="p-3 text-left border-2 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                      onClick={() => setNewTransferData({
                        ...newTransferData,
                        branch_id: branch.id,
                        branch_name: branch.name,
                        branch_address: `${branch.address1}, ${branch.address2}, ${branch.city}, ${branch.postcode}`,
                        branch_contact: branch.contact || '',
                        branch_phone: branch.phone || ''
                      })}
                    >
                      <p className="font-semibold text-sm">{branch.name}</p>
                      <p className="text-xs text-gray-500">{branch.city}, {branch.postcode}</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Selected Branch Info */}
                <div className="flex items-start justify-between bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                  <div>
                    <p className="font-bold text-blue-900">{newTransferData.branch_name}</p>
                    <p className="text-sm text-gray-600">{newTransferData.branch_address}</p>
                    {newTransferData.branch_contact && (
                      <p className="text-sm text-gray-600 mt-1">Contact: {newTransferData.branch_contact}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewTransferData({...newTransferData, branch_id: '', branch_name: ''})}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Requester Name */}
                <div>
                  <Label>Requested By *</Label>
                  <Input
                    value={newTransferData.requested_by}
                    onChange={(e) => setNewTransferData({...newTransferData, requested_by: e.target.value})}
                    placeholder="Enter your name"
                    className="mt-1"
                  />
                </div>

                {/* Collection Date */}
                <div>
                  <Label>Collection Date (optional)</Label>
                  <Input
                    type="date"
                    value={newTransferData.collection_date}
                    onChange={(e) => setNewTransferData({...newTransferData, collection_date: e.target.value})}
                    className="mt-1"
                  />
                </div>

                {/* Products */}
                <div>
                  <Label className="text-base font-semibold mb-2 block">Products</Label>
                  <div className="space-y-2">
                    {newTransferData.items.map((item, index) => (
                      <div key={item.id} className="flex items-center gap-2">
                        <Input
                          value={item.product_code}
                          onChange={(e) => updateTransferItem(index, 'product_code', e.target.value)}
                          placeholder="Code"
                          className="w-24 text-sm font-mono"
                        />
                        <Input
                          value={item.description}
                          onChange={(e) => updateTransferItem(index, 'description', e.target.value)}
                          placeholder="Description"
                          className="flex-1 text-sm"
                        />
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateTransferItem(index, 'quantity', e.target.value)}
                          className="w-16 text-center"
                        />
                        <button
                          type="button"
                          onClick={addTransferItem}
                          className="w-7 h-7 rounded-full flex items-center justify-center text-white bg-blue-500"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => removeTransferItem(index)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-white bg-red-500"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes (optional)</Label>
                  <textarea
                    value={newTransferData.notes}
                    onChange={(e) => setNewTransferData({...newTransferData, notes: e.target.value})}
                    placeholder="Additional notes..."
                    className="mt-1 w-full p-3 border rounded-lg resize-none h-16"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 py-4 rounded-full border-2"
                    onClick={() => setShowCreateTransferModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 py-4 rounded-full text-white font-semibold"
                    style={{ backgroundColor: '#3b82f6' }}
                    onClick={handleSaveNewTransfer}
                    disabled={savingTransfer}
                  >
                    {savingTransfer ? 'Creating...' : 'Create & Link Transfer'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* BI EXT Transport / Ops Review Modal */}
      <OpsReviewModal
        open={biExtTransportModalOpen}
        onClose={() => setBiExtTransportModalOpen(false)}
      />
    </div>
  );
};

export default POExpenses;
